import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import Button from "@/components/ui/button";
import PageHeader from "@/components/ui/page-header";
import TabGroup from "@/components/ui/tab";
import CatalogCard from "@/components/ui/catalog-card";
import type { ViewMode, FilterField, SortOption } from "@/types/component";
import {
  educationCatalogApi,
  type CatalogCardItemWithKey,
} from "../services/education-catalog.api";
import useNotification from "@/hooks/use-notification";
import { formatDurationFromParts } from "@/utils/format-duration";
import styles from "./EducationCatalogPage.module.scss";

// Backend type: 0=ON_SITE, 1=ONLINE, 2=UNIT, 3=NONUNIT
const TAB_TYPE_MAP: (number | null)[] = [null, 2, 3, 0, 1]; // Tümü, Üniteli, Ünitesiz, Yüz Yüze, Canlı(Online)

// Backend sortBy: createdAt | requestedEnrollmentsCount | duration | requestCollectionEndDate
const SORT_MAP: Record<string, { sortBy: string; sortOrder: "asc" | "desc" }> =
  {
    // Varsayılan "En Yeni" → son oluşturulanlar
    newest: { sortBy: "createdAt", sortOrder: "desc" },
    popular: { sortBy: "requestedEnrollmentsCount", sortOrder: "desc" },
    upcoming: { sortBy: "requestCollectionEndDate", sortOrder: "asc" },
    duration: { sortBy: "durationMinutes", sortOrder: "asc" },
  };

const EducationCatalogPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notifySuccess, notifyError } = useNotification();

  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [activeTab, setActiveTab] = useState(0);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const [catalogItems, setCatalogItems] = useState<CatalogCardItemWithKey[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sortOrder, setSortOrder] = useState("newest");
  const [categoryOptions, setCategoryOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [instructorOptions, setInstructorOptions] = useState<
    { label: string; value: string }[]
  >([]);

  useEffect(() => {
    educationCatalogApi
      .getEducationCategories()
      .then((categories) => {
        setCategoryOptions(
          categories
            .map((c) => ({
              label: c.name ?? c.label ?? c.id ?? "",
              value: c.id ?? "",
            }))
            .filter((o) => o.label && o.value),
        );
      })
      .catch(() => setCategoryOptions([]));

    educationCatalogApi
      .getInstructors()
      .then((instructors) => {
        setInstructorOptions(
          instructors
            .map((i) => ({
              label:
                [i.firstName, i.lastName].filter(Boolean).join(" ") || i.id,
              value: i.id,
            }))
            .filter((o) => o.value),
        );
      })
      .catch(() => setInstructorOptions([]));
  }, []);

  const fetchCatalog = useCallback(async () => {
    const isFirstPage = page === 1;
    if (isFirstPage) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }
    setError(null);
    try {
      const type = TAB_TYPE_MAP[activeTab] ?? null;
      const categoryIds = Array.isArray(filterValues.category)
        ? filterValues.category
        : filterValues.category
          ? [filterValues.category]
          : undefined;
      const instructorIds = Array.isArray(filterValues.educator)
        ? filterValues.educator
        : filterValues.educator
          ? [filterValues.educator]
          : undefined;

      const sortParams = SORT_MAP[sortOrder];

      const { data, total: totalCount } = await educationCatalogApi.getCatalog({
        page,
        limit: 12,
        search: searchQuery || undefined,
        type: type ?? undefined,
        categoryIds: categoryIds?.length ? categoryIds : undefined,
        instructorIds: instructorIds?.length ? instructorIds : undefined,
        sortBy: sortParams?.sortBy,
        sortOrder: sortParams?.sortOrder,
      });
      setCatalogItems((prev) => (isFirstPage ? data : [...prev, ...data]));
      setTotal(totalCount);
    } catch (err: any) {
      const message =
        err?.response?.data?.message || err?.message || t("common.error");
      setError(message);
      if (isFirstPage) {
        setCatalogItems([]);
      }
    } finally {
      if (isFirstPage) {
        setIsLoading(false);
      } else {
        setIsLoadingMore(false);
      }
    }
  }, [page, searchQuery, activeTab, filterValues, sortOrder, t]);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchTerm);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleRequestEnrollment = async (id: string) => {
    try {
      await educationCatalogApi.requestEnrollment(id);
      fetchCatalog();
      notifySuccess(
        t("educationCatalog.enrollment.requestSuccess"),
      );
    } catch (err: any) {
      const message =
        err?.response?.data?.message || err?.message || t("common.error");
      notifyError(message);
    }
  };

  const tabs = [
    { label: t("educationCatalog.tabs.all"), content: null },
    { label: t("educationCatalog.tabs.uniteli"), content: null },
    { label: t("educationCatalog.tabs.nonUnit"), content: null },
    { label: t("educationCatalog.tabs.faceToFace"), content: null },
    { label: t("educationCatalog.tabs.live"), content: null },
  ];

  const filterFields: FilterField[] = [
    {
      name: "category",
      label: t("educationCatalog.filters.category"),
      type: "dropdown",
      placeholder: t("educationCatalog.filters.categoryPlaceholder"),
      options: categoryOptions,
      searchable: true,
      multiple: true,
    },
    // {
    //   name: "duration",
    //   label: t("educationCatalog.filters.duration"),
    //   type: "dropdown",
    //   placeholder: t("educationCatalog.filters.durationPlaceholder"),
    //   multiple: true,
    //   options: [
    //     { label: "1 Saat", value: "1" },
    //     { label: "2 Saat", value: "2" },
    //     { label: "3 Saat", value: "3" },
    //     { label: "4 Saat", value: "4" },
    //     { label: "5+ Saat", value: "5+" },
    //   ],
    // },
    {
      name: "educator",
      label: t("educationCatalog.filters.educator"),
      type: "dropdown",
      placeholder: t("educationCatalog.filters.educatorPlaceholder"),
      options: instructorOptions,
      searchable: true,
      multiple: true,
    },
  ];

  const sortOptions: SortOption[] = [
    { label: t("educationCatalog.sort.newest"), value: "newest" },
    { label: t("educationCatalog.sort.popular"), value: "popular" },
    { label: t("educationCatalog.sort.upcoming"), value: "upcoming" },
    { label: t("educationCatalog.sort.duration"), value: "duration" },
  ];

  const handleTabChange = (index: number) => {
    setActiveTab(index);
    setPage(1);
  };

  const handleFilterChange = (filters: Record<string, any>) => {
    setFilterValues(filters);
    setPage(1);
  };

  const handleSortChange = (option: SortOption) => {
    setSortOrder(option.value);
    setPage(1);
  };

  const handleLoadMore = () => {
    if (isLoadingMore) return;
    setPage((prev) => prev + 1);
  };

  const hasMore = catalogItems.length < total;

  return (
    <DashboardLayout>
      <Header title={t("educationCatalog.title")} />
      <div className={styles.filterContainer}>
        <TabGroup
          tabs={tabs}
          defaultActiveTab={activeTab}
          onTabChange={handleTabChange}
          orientation="horizontal"
        />

        <PageHeader
          searchPlaceholder={t("educationCatalog.searchPlaceholder")}
          searchValue={searchTerm}
          onSearch={setSearchTerm}
          filters={filterFields}
          onFilterChange={handleFilterChange}
          showViewToggle
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          showSort
          sortOptions={sortOptions}
          onSort={handleSortChange}
        />
      </div>

      {error && <div className={styles.errorMessage}>{error}</div>}

      {isLoading ? (
        <div className={styles.loadingState}>{t("common.loading")}</div>
      ) : (
        <>
          <div className={`${styles.catalogGrid} ${styles[viewMode]}`}>
            {catalogItems.map((item) => {
              const {
                buttonLabelKey,
                buttonAction,
                durationHours,
                durationMinutes,
                ...rest
              } = item;
              const durationLabel = formatDurationFromParts(
                durationHours,
                durationMinutes,
              );
              const onButtonClick = item.buttonDisabled
                ? undefined
                : () => {
                    switch (buttonAction) {
                      case "exam":
                        navigate(`/dashboard/client-exams/take/${item.id}`);
                        break;
                      case "survey":
                        navigate("/dashboard/client-polls", {
                          state: {
                            educationId: item.id,
                            pollTitle: rest.title,
                          },
                        });
                        break;
                      case "detail":
                        navigate(
                          `/dashboard/education-catalog/detail?id=${item.id}`,
                        );
                        break;
                      case "request":
                      default:
                        handleRequestEnrollment(item.id);
                    }
                  };
              return (
                <CatalogCard
                  key={item.id}
                  {...rest}
                  duration={durationLabel}
                  buttonLabel={t(buttonLabelKey)}
                  onButtonClick={onButtonClick}
                />
              );
            })}
          </div>
          {hasMore && (
            <div className={styles.loadMore}>
              <Button
                variant="outlined"
                color="default"
                onClick={handleLoadMore}
                loading={isLoadingMore}
              >
                {t("common.loadMore")}
              </Button>
            </div>
          )}
        </>
      )}

      {!isLoading && !error && catalogItems.length === 0 && (
        <div className={styles.emptyState}>
          {t("educationCatalog.noResults")}
        </div>
      )}
    </DashboardLayout>
  );
};

export default EducationCatalogPage;
